import { AlertColor, Box, Grid, Skeleton, Typography } from "@mui/material";
import { EmptyContainer } from "../../../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";
import { useTranslation } from "react-i18next";
import { NoWorkspacesOrganizationIcon } from "../../../../../assets/svg/NoWorkspacesOrganizationIcon";
import { useCallback, useEffect, useRef, useState } from "react";
import { DashboardCardOrganization, IDashboardCardOrganization } from "../DashboardCard/DashboardCardOrganization";
import { Organization } from "../../../../../model/OrganizationModel";
import { StorageSizeInfoModel } from "../../../../../model/StorageSizeInfoModel";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import { KeyDriveSearchBar } from "../../../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBar";
import { useProfile } from "../../../../../context/ProfileProvider/useProfile";
import { isUserOrganizationAdministrator } from "../../../../../helpers/OrganizationHelpers";
import { GetAdminList, GetAdminListName } from "../../../../../services/OrganizationServices";

interface IDashboardCardOrganizationListProps {
    generalData: StorageSizeInfoModel;
}

function useVisibility(
  cb: (isVisible: boolean) => void,
  deps: React.DependencyList
): (node: any) => void {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  return useCallback((node) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      cb(entry.isIntersecting);
    });

    if (node) intersectionObserver.current.observe(node);
  }, deps);
}

export const DashboardCardOrganizationList = ({ generalData }: IDashboardCardOrganizationListProps) => {
    const { t } = useTranslation();
    const [skip, setSkip] = useState<number>(0);
    const LIMIT = 25;
    const [organizationMax, setOrganizationMax] = useState<boolean>(true);
    const [dataIntern, setDataIntern] = useState<IDashboardCardOrganization[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [alertColor, setAlertColor] = useState<AlertColor>("success");
    const [organizatinSearchTerm, setOrganizationSearchTerm] = useState<string>("");
    const profile = useProfile();

    const convertOrganizationDashboardData = (response: Organization[]): IDashboardCardOrganization[] => {
        
        if (response.length == 0) return [];

        let data: IDashboardCardOrganization[] = [];
        
        for(let i = 0; i <= response.length; i++) {

            let users = getOrgUsers(response[i]);

            if (isUserOrganizationAdministrator(response[i], profile.id)) {
                data.push({
                    name: response[i]?.name!,
                    users: users,
                    workspaceQuantity: response[i]?.workspaceQuantity ?? 0
                });
            }

        }

        return data;
    }

    function getOrgUsers(organization: Organization): number {
        let users: number = 0;

        users += organization?.adminUsers?.length ?? 0;
        users += organization?.ordinaryUsers?.length ?? 0;
        
        return users;
    }

    useEffect(() => {
        setIsLoading(true);
        setSkip(0);
        setDataIntern([]);

        if (organizatinSearchTerm == "") {
            setOrganizationMax(true);
            GetAdminList(skip, LIMIT, false)
            .then(response => {

                if (response.length < LIMIT) {
                    setOrganizationMax(false);
                }

                let orgData = convertOrganizationDashboardData(response);
                setDataIntern(orgData);
            
            }).catch((err) =>{
                setMessage(err.response.data);
                setAlertColor("error");
            })
            .finally(() => {
                setIsLoading(false);
            })
            
            return;
        }

        GetAdminListName(skip, LIMIT, false, organizatinSearchTerm)
        .then(response => {
            let orgData = convertOrganizationDashboardData(response);
            setDataIntern(orgData);
        }).finally(() => {
            setIsLoading(false);
        })
    }, [organizatinSearchTerm])

    const lastBook = useVisibility(
        (visible) => {
            if (visible) {
                if (organizatinSearchTerm == "") {
                    GetAdminList(skip, LIMIT, false)
                    .then(response => {
                        if (response.length < LIMIT) {
                            setOrganizationMax(false);
                        }

                        setSkip(prev => prev + response.length);
                        let orgData = convertOrganizationDashboardData(response);
                        setDataIntern(previous => [...previous, ...orgData]);
                    })
                } else {
                    GetAdminListName(skip, LIMIT, false, organizatinSearchTerm)
                    .then(response => {
                        if (response.length < LIMIT) {
                            setOrganizationMax(false);
                        }

                        setSkip(prev => prev + response.length);
                        let orgData = convertOrganizationDashboardData(response);
                        setDataIntern(previous => [...previous, ...orgData]);
                    })
                }
            }
        }, [skip, dataIntern]);

    const renderContent = () => {
        if (isLoading) {
            return Array.from(Array(6)).map((_, index) => (
                <Grid item xs={2} sm={4} md={4} key={index}>
                    <Box
                        sx={{
                            background: "#FFFFFF 0% 0% no-repeat padding-box",
                            borderRadius: "4px",
                            opacity: 1,
                            border: "1px solid #0000001A",
                            width: "100%",
                            height: "fit-content",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "12px",
                        }}
                    >
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: "2px" }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Skeleton variant="text" width="30%" height={16} />
                            <Skeleton variant="text" width="30%" height={16} />
                        </Box>
                    </Box>

                </Grid>
            ));
        }

        if (dataIntern) {
            if (dataIntern.length <= 0 && !isLoading) {
                return (
                    <Box width="100%" marginTop={2.5} height="100%">
                        <EmptyContainer
                            content={t('AdminConsole.NoFoundWorkspaceSubtittle')}
                            title={t('AdminConsole.NotFoundWorkspace')}
                            icon={<NoWorkspacesOrganizationIcon />}
                        />
                    </Box>
                );
            }

            return dataIntern.map((item, index) => (
                <Grid item xs={2} sm={4} md={4} key={index} sx={{ width: "100%" }}  ref={dataIntern[dataIntern.length - 1]?.name === item?.name && organizationMax ? lastBook : null}>
                    <DashboardCardOrganization {...item} />
                </Grid>
            ));
        }

        return null;
    };


    return (
        <>
            <KeyDriveSearchBar 
                value={organizatinSearchTerm}
                onChange={(e) => { setOrganizationSearchTerm(e.target.value) }}
            />
            <Box sx={{ margin: '5px 0 25px 0' }} display={"flex"} justifyContent="space-between" alignItems="top">
                <Typography
                    sx={{
                        font: "normal normal 600 16px/21px Segoe UI",
                        letterSpacing: "0px",
                        color: "#2D2D2D",
                    }}
                >
                    {t("AdminConsole.Organizations")}
                </Typography>
            </Box>
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 8, lg: 12 }} sx={{ overflowY: 'auto', height: "fit-content", paddingRight: '8px' }}>
                {renderContent()}
            </Grid>
            <KeydriveSnackbar 
                open={message != undefined}
                message={message!}
                onClose={() => setMessage(undefined)}
                variant={alertColor}
            />
        </>
    );
};
